<template>
  <v-dialog
      v-model="dialog"
      :width="dialogWidth"
      @keydown.enter.prevent="onenter"
  >
    <v-card
        class="resizable-card"
        :class="{ 'no-select': resizing }"
        :style="{ height: dialogHeight + 'px' }"
    >
      <v-card-title>
        <span class="headline">Trader Config ({{ this.rules.name }})</span>
        <v-spacer/>
        <r-authorize
            :master="masterAccess"
            :locked.sync="model.locked"
            :enabled.sync="model.enabled"
        />
      </v-card-title>
      <v-card-text>
        <v-col cols="4" sm="2" md="2">
          <v-btn @click="showPreview()" class="mr-2" small>View JSON</v-btn>
        </v-col>
        <v-row dense class="mt-3">
          <v-col cols="12" md="12" sm="12">
            <v-text-field disabled label="UID:" v-model.trim="model.uid" hide-details outlined dense/>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="6" sm="6">
            <v-text-field
                label="Name:"
                v-model.trim="model.name"
                v-ascii-only
            />
          </v-col>
          <v-col cols="12" md="6" sm="6">
            <r-ship-kind :model.sync="model.trader" label="Trader:"/>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="2" sm="2">
            <v-text-field label="Quantity:" v-model.number="model.quantity"/>
          </v-col>
          <v-col cols="12" md="2" sm="2">
            <v-text-field label="Start time:" v-model.number="model.startTime" hide-details/>
          </v-col>
          <v-col cols="12" md="2" sm="2">
            <v-text-field label="Safe time:" v-model.number="model.safeTime" hide-details/>
          </v-col>
          <v-col cols="12" md="2" sm="2">
            <v-text-field label="Duration:" v-model.number="model.duration" hide-details/>
          </v-col>
        </v-row>
        <v-row dense>
          <v-expansion-panels>
            <r-guard-panel :model.sync="model.guard"/>
            <v-expansion-panel>
              <v-expansion-panel-header v-slot="{ open }">
                <v-row no-gutters>
                  <span style="margin-top: 5px">Orders Config</span>
                  <v-spacer/>
                  <v-fade-transition>
                    <span style="display: inline-flex" v-if="open">
                      <v-divider class="mx-1" vertical/>
                    </span>
                  </v-fade-transition>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row dense>
                  <v-col cols="12" md="3" sm="3">
                    <v-text-field label="Count:" v-model.number="model.ordersCount" hide-details/>
                  </v-col>
                </v-row>
                <v-row dense>
                  <v-col cols="12" md="12" sm="12">
                    <r-trader-order-table :items.sync="model.ordersCustom" title="Custom"/>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header v-slot="{ open }">
                <v-row no-gutters>
                  <span style="margin-top: 5px">Offers Config</span>
                  <v-spacer/>
                  <v-fade-transition>
                    <span style="display: inline-flex" v-if="open">
                      <v-divider class="mx-1" vertical/>
                    </span>
                  </v-fade-transition>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row dense>
                  <v-col cols="12" md="3" sm="3">
                    <v-text-field label="Count:" v-model.number="model.offersCount" hide-details/>
                  </v-col>
                </v-row>
                <v-row dense>
                  <v-col cols="12" md="12" sm="12">
                    <r-trader-offer-rule-table :items.sync="model.offersRules" title="Rules"/>
                  </v-col>
                </v-row>
                <v-row dense>
                  <v-col cols="12" md="12" sm="12">
                    <r-trader-offer-table :items.sync="model.offersRequired" title="Custom"/>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <r-reward-panel :model.sync="model.ordersReward" title="Orders Reward"/>
            <r-reward-panel :model.sync="model.offersReward" title="Offers Reward"/>
          </v-expansion-panels>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer/>
        <v-btn @click="cancel" color="blue darken-1" text>
          Cancel
        </v-btn>
        <v-btn @click="save" color="blue darken-1" text v-if="!lockedAssess || masterAccess">
          Save
        </v-btn>
      </v-card-actions>
      <div class="resize-handle" @mousedown="startResize"></div>
      <r-preview-dialog ref="preview-dialog"></r-preview-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import {ViewRules} from "@/common/ViewRules";
import {TraderConfig} from "@/modules/redactor/models/trader/TraderConfig";
import {RedactorUtils} from "@/modules/redactor/core/RedactorUtils";

import RGuardPanel from "@/modules/redactor/views/guard/r-guard-panel";
import RShipKind from "@/modules/redactor/views/trader/r-ship-kind";
import RRewardPanel from "@/modules/redactor/views/reward/r-reward-panel.vue";
import RAuthorize from "@/modules/redactor/views/common/r-authorize";
import RTraderOrderTable from "@/modules/redactor/views/trader/r-trader-order-table";
import RTraderOfferTable from "@/modules/redactor/views/trader/r-trader-offer-table";
import RTraderOfferRuleTable from "@/modules/redactor/views/trader/r-trader-offer-rule-table";
import RPreviewDialog from "@/modules/redactor/views/preview/r-preview-dialog.vue";
import {MapAddonGroupModel} from "@/modules/redactor/models/map-addon/core/MapAddonGroupModel";

export default {
  name: "r-trader-config-dialog",
  components: {
    RPreviewDialog,
    RTraderOfferRuleTable,
    RTraderOfferTable,
    RTraderOrderTable,
    RAuthorize,
    RRewardPanel,
    RShipKind,
    RGuardPanel
  },
  data: () => ({
    dialog: false,
    resolve: null,
    reject: null,
    model: new TraderConfig(),
    rules: ViewRules.READONLY,
    dialogWidth: 633,
    dialogHeight: 676,
    resizing: false,
    resizeStartX: 0,
    resizeStartY: 0,
    resizeStartWidth: 633,
    resizeStartHeight: 676,
    viewportPadding: 32
  }),
  computed: {
    lockedAssess() {
      return this.model.locked;
    },
    masterAccess() {
      return RedactorUtils.hasMasterAccess();
    }
  },
  methods: {
    show(rules, model) {
      this.dialog = true;
      this.rules = rules;
      this.model = model || new TraderConfig();

      if (ViewRules.COPY.equal(this.rules)) {
        this.model.locked = false;
      }

      return new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      });
    },
    save() {
      if (this.model.valid()) {
        this.resolve(this.model);
        this.discard();
      } else {
        this.$alert("Invalid config!");
      }
    },
    cancel() {
      this.reject();
      this.discard();
    },
    discard() {
      this.dialog = false;
      this.stopResize();
    },
    onenter() {
      if (!this.lockedAssess || this.masterAccess) {
        this.save();
      } else {
        this.cancel();
      }
    },
    startResize(e) {
      this.resizing = true;
      this.resizeStartX = e.clientX;
      this.resizeStartY = e.clientY;
      this.resizeStartWidth = this.dialogWidth;
      this.resizeStartHeight = this.dialogHeight;
      window.addEventListener("mousemove", this.onResize);
      window.addEventListener("mouseup", this.stopResize);
    },
    onResize(e) {
      if (!this.resizing) return;

      const dx = e.clientX - this.resizeStartX;
      const dy = e.clientY - this.resizeStartY;

      let newWidth = this.resizeStartWidth + dx;
      let newHeight = this.resizeStartHeight + dy;

      const minWidth = 450;
      const minHeight = 380;

      if (newWidth < minWidth) newWidth = minWidth;
      if (newHeight < minHeight) newHeight = minHeight;

      this.dialogWidth = newWidth;
      this.dialogHeight = newHeight;
    },
    stopResize() {
      if (!this.resizing) return;
      this.resizing = false;
      window.removeEventListener("mousemove", this.onResize);
      window.removeEventListener("mouseup", this.stopResize);
    },
    showPreview() {
      this.$refs['preview-dialog'].show(this.rules, this.model, TraderConfig)
        .then((result) => {
          Object.assign(this.model, result);
        })
        .catch((e) => {
          this.$alert("Failed to parse config!" + e.message);
        });
    }
  },
  beforeDestroy() {
    this.stopResize();
  }
};
</script>

<style scoped>
.resizable-card {
  position: relative;
  display: flex;
  flex-direction: column;
}
.resize-handle {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 14px;
  height: 14px;
  cursor: se-resize;
  border-right: 2px solid rgba(0, 0, 0, 0.3);
  border-bottom: 2px solid rgba(0, 0, 0, 0.3);
}
.no-select {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}
</style>